import { useMemo } from 'react';
import { useJpa, useRequest } from '@stores';
import { connect } from 'redux-bundler-react';

import PreAppForm3Instructions from '@forms/static-forms/PreAppForm3Instructions/PreAppForm3Instructions';
import GeneralProjectInformation from '@forms/input-forms/common/GeneralProjectInformation';
import PreAppInformation from '@forms/input-forms/PreAppForm/PreAppInformation';
import Contacts from '@forms/input-forms/common/Contacts';
import PreAppFormPropertyOwners from '@pages/Forms/input-forms/PreAppForm/PreAppFormPropertyOwners';
import SupportingInformation from '@forms/input-forms/common/SupportingInformation';
import PreAppForm3CertifySignSubmit from '@pages/Forms/input-forms/PreAppForm/PreAppForm3CertifySignSubmit';
import JpaInfo from './JpaInfo';
import JpaForm, { generateJpaFormName } from './JpaForm';

//List of steps for vertical stepper, these can also be used for form header. index = step ID
export const getPreAppForm3Metadata = () => {
  const baseFormArray = [
    PreAppForm3Instructions.metadata,
    Contacts.metadata,
    GeneralProjectInformation.metadata,
    PreAppInformation.metadata,
    PreAppFormPropertyOwners.metadata,
    SupportingInformation.metadata,
    PreAppForm3CertifySignSubmit.metadata,
  ];
  const jpaBasicInfo = useJpa.getState().jpaBasicInfo;
  const isJpa = jpaBasicInfo?.length > 0;

  // insert JPA sidebar steps
  if (isJpa) {
    const jpaInfoSteps = [
      {
        sectionName: 'Location Specific Information',
        isParentStep: true,
      },
    ];
    const jpaFormSteps = [
      {
        sectionName: 'Location Specific Questions',
        isParentStep: true,
      },
    ];

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: 1,
      });
      jpaFormSteps.push({
        sectionName: generateJpaFormName(jpa),
        isSubStep: true,
        parentId: jpaBasicInfo?.length + baseFormArray.length - 1,
      });
    });

    baseFormArray.splice(1, 0, ...jpaInfoSteps);
    baseFormArray.splice(baseFormArray.length - 2, 0, ...jpaFormSteps);
  }

  return baseFormArray;
};

const generateSections = ({ doUpdatePreAppRequest, isJpa, jpaBasicInfo, preAppData, preAppID }) => {
  const sections = [
    {
      id: 'PreAppForm3Instructions',
      name: 'Instructions',
      content: PreAppForm3Instructions,
    },
    {
      id: 'PreAppForm3ContactInformation',
      name: 'Contact Information',
      content: Contacts,
      subtext: 'requesting a Pre-Application/Project Scoping meeting regarding the proposed activity.',
      componentID: preAppID,
    },
    {
      id: 'GeneralProjectInformation',
      name: 'General Project Information',
      content: GeneralProjectInformation,
    },
    {
      id: 'PreAppInformation',
      name: 'Pre App Information',
      content: PreAppInformation,
      componentID: preAppID,
    },
    {
      id: 'PreAppForm3PropertyOwners',
      name: 'Property Owners',
      content: PreAppFormPropertyOwners,
      componentID: preAppID,
    },
    {
      id: 'PreAppForm3SupportingInformation',
      name: 'Supporting Information',
      content: SupportingInformation,
      componentID: preAppID,
      subtext: (
        <>
          <p>
            To facilitate your request, we strongly encourage a copy of the Delineation Report and supporting
            information be uploaded in this section. The types of supporting information that should be provided are
            contained in the drop-down list when you click on the "Add a Document" tab. Information that is most helpful
            in the USACE review are aerial photographs, automated wetland data sheets, and maps/plans/plots. Multiple
            documents of the same file type can be combined and uploaded as one document.
          </p>
        </>
      ),
      updateRequestData: doUpdatePreAppRequest,
    },
    {
      id: 'PreAppForm3CertifySignSubmit',
      name: 'Certify Sign Submit',
      content: PreAppForm3CertifySignSubmit,
    },
  ];
  // insert JPA content
  if (isJpa) {
    const jpaInfoSections = [
      {
        id: 'jpa-info',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];
    const jpaFormSections = [
      {
        id: 'jpa-questions',
        name: 'Location Specific Information',
        content: () => <></>,
      },
    ];

    const infoStartIndex = 1;
    const formStartIndex = sections.length + jpaBasicInfo.length - 1;
    const infoGroupIndexes = [];
    const formGroupIndexes = [];

    jpaBasicInfo.forEach((_, jpaIndex) => {
      infoGroupIndexes.push(jpaIndex + infoStartIndex + 1);
      formGroupIndexes.push(jpaIndex + formStartIndex + 1);
    });

    jpaBasicInfo.forEach((jpa) => {
      jpaInfoSections.push({
        id: `jpa-info-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaInfo,
        componentId: preAppID,
        formId: jpa.formID,
        group: {
          indexes: infoGroupIndexes,
          parentIndex: infoStartIndex,
          parentName: jpaInfoSections[0].name,
        },
        requestId: preAppData.requestID,
        version: preAppData.version,
      });
      jpaFormSections.push({
        id: `jpa-questions-${jpa.formID}`,
        name: generateJpaFormName(jpa),
        content: JpaForm,
        componentId: preAppID,
        formId: jpa.formID,
        group: {
          indexes: formGroupIndexes,
          parentIndex: formStartIndex,
          parentName: jpaFormSections[0].name,
        },
        requestId: preAppData.requestID,
        version: preAppData.version,
      });
    });

    sections.splice(1, 0, ...jpaInfoSections);
    sections.splice(sections.length - 2, 0, ...jpaFormSections);
  }

  return sections;
};

const PreAppForm3 = connect(
  'doUpdatePreAppRequest',
  'selectPreAppID',
  'selectPreAppData',
  ({ doUpdatePreAppRequest, preAppID, preAppData }) => {
    const { getActiveSection } = useRequest();
    const { jpaBasicInfo } = useJpa();
    const isJpa = jpaBasicInfo?.length > 0;
    const activeStep = getActiveSection();

    const sections = useMemo(
      () => generateSections({ doUpdatePreAppRequest, isJpa, jpaBasicInfo, preAppData, preAppID }),
      [doUpdatePreAppRequest, isJpa, jpaBasicInfo, preAppData, preAppID]
    );

    return sections.map((section, index) => {
      const { content: Content, ...props } = section;

      return (
        <section
          id={section.id}
          key={section.id}
          name={section.name}
          style={{ display: activeStep?.id === index ? 'inline' : 'none' }}
        >
          <Content stepNo={index} {...props} {...section.props} />
        </section>
      );
    });
  }
);

export default PreAppForm3;
